import React from 'react';
import { useSelector } from 'react-redux';

import { selectRoles } from '../../features/auth/auth.slice';
import { GeneralConstants } from '../constants/general-constants';
import { Role } from '../constants/role';
import { RoleAccessUtils } from '../utils/role-access-utils';

export interface RequireRoleProps {
    /**
     * If true then user must have ALL specified roles, otherwise at least one.
     */
    all?: true;

    /**
     * Required role to render the contents
     */
    roles?: Role | Role[];

    /**
     * If provided this node will be rendered when user has insufficient access.
     */
    insufficientAccessRender?: React.ReactElement;
}

const hasSufficientAccess = (currentRoles: Record<string, boolean>, roles?: Role | Role[], all?: boolean): boolean => {
    if (!roles) {
        return true;
    }

    if (all) {
        return RoleAccessUtils.hasAllRoles(currentRoles, roles);
    }

    return RoleAccessUtils.hasAnyRole(currentRoles, roles);
};

export const RequireRole: React.FC<RequireRoleProps> = React.forwardRef(
    ({ all, roles, insufficientAccessRender, children, ...rest }, ref) => {
        const currentRoles = useSelector(selectRoles);

        if (hasSufficientAccess(currentRoles, roles, all)) {
            return (
                <>
                    {React.Children.map(children, (child) => {
                        if (!React.isValidElement(child)) {
                            return GeneralConstants.RENDER_NOTHING;
                        }

                        return React.cloneElement(child, {
                            ref,
                            ...child.props,
                            ...rest,
                        } as Partial<typeof child>);
                    })}
                </>
            );
        }

        if (insufficientAccessRender) {
            // if parent specified to render something on insufficient access, do so
            return insufficientAccessRender;
        }

        return GeneralConstants.RENDER_NOTHING;
    },
);
